import cx from 'classnames';
import type { FunctionComponent } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { FormattedMessage } from 'react-intl';

import ShortcutIcon from '@atlaskit/icon/core/link-external';
import { Analytics, formatContainers } from '@trello/atlassian-analytics';
import { dynamicToken } from '@trello/dynamic-tokens';
import { useFeatureGate } from '@trello/feature-gate-client';
import { intl } from '@trello/i18n';
import type { JwmLinkNavData } from '@trello/jwm';
import {
  useCanInvite,
  useJwmAdminhubInviteDeeplink,
  useJwmLinkOrganization,
} from '@trello/jwm';
import { AddMemberIcon } from '@trello/nachos/icons/add-member';

import { InviteToJiraButton } from 'app/src/components/InviteToJira/';
import { JoinJiraButton } from 'app/src/components/JoinJiraButton';

import * as styles from './JwmLinkedWorkspace.module.less';

const analyticsSource = 'currentWorkspaceNavigationDrawer';

interface JwmLinkedWorkspaceProps {
  workspaceId?: string;
  jwmLinkNavigationData: JwmLinkNavData;
  isNewWorkspacePage?: boolean;
}

export const JwmLinkedWorkspace: FunctionComponent<JwmLinkedWorkspaceProps> = ({
  workspaceId,
  jwmLinkNavigationData,
  isNewWorkspacePage = false,
}) => {
  const { value: isInviteExperienceEnabled } = useFeatureGate(
    'trello_xf_invite_experience_2',
  );
  const { adminhubInviteUrl, showInviteButton } =
    useJwmAdminhubInviteDeeplink(workspaceId);
  const [joined, setJoined] = useState(false);

  const jwmLinkData = useJwmLinkOrganization(workspaceId);
  const { canInvite } = useCanInvite(jwmLinkData?.idCloud);

  const openJwmLinkedSite = useCallback(
    function openJwmLinkedSite() {
      Analytics.sendClickedLinkEvent({
        linkName: 'jwmLinkedWorkspaceLink',
        source: analyticsSource,
        containers: formatContainers({ idOrganization: workspaceId }),
      });
    },
    [workspaceId],
  );

  const openAdminhubInviteLink = useCallback(() => {
    Analytics.sendClickedLinkEvent({
      linkName: 'jwmLinkedWorkspaceAdminhubInviteLink',
      source: analyticsSource,
      containers: formatContainers({ idOrganization: workspaceId }),
    });
  }, [workspaceId]);

  const inviteLink = useMemo(
    () => (
      <a
        href={adminhubInviteUrl}
        target="_blank"
        aria-label={intl.formatMessage({
          id: 'templates.jwm_site_workspace_link.aria-label-workspace-jwm-invite-spork',
          defaultMessage:
            'Invite members to the linked Jira site. Opens external site.',
          description:
            'Invite members to linked Jira site, opens external site.',
        })}
        onClick={openAdminhubInviteLink}
        className={cx(
          styles.workspaceLink,
          isNewWorkspacePage && styles.newWorkspacePage,
        )}
      >
        <div className={styles.shortcutIconFlagEnabled}>
          <AddMemberIcon
            label=""
            size="small"
            color={dynamicToken('dynamic.icon')}
          />
        </div>
        <p className={styles.workspaceLinkText}>
          <FormattedMessage
            id="templates.jwm_site_workspace_link.invite-members"
            defaultMessage="Invite members"
            description="Link to invite new members to the workspace."
          />
        </p>
      </a>
    ),
    [adminhubInviteUrl, isNewWorkspacePage, openAdminhubInviteLink],
  );

  const renderInviteExperience = useCallback(() => {
    if (isInviteExperienceEnabled && canInvite) {
      return (
        <InviteToJiraButton
          workspaceId={workspaceId}
          isNewWorkspacePage={isNewWorkspacePage}
        />
      );
    }

    if (!showInviteButton) {
      return null;
    }

    return inviteLink;
  }, [
    canInvite,
    inviteLink,
    isInviteExperienceEnabled,
    showInviteButton,
    workspaceId,
    isNewWorkspacePage,
  ]);

  return (
    <>
      {jwmLinkNavigationData.showJoinButton && !joined ? (
        <div className={styles.workspaceLinkRow}>
          <div className={styles.shortcutIconFlagEnabled}>
            <ShortcutIcon
              label=""
              // @ts-expect-error
              color={dynamicToken('dynamic.icon')}
              size="small"
            />
          </div>
          <p className={styles.workspaceLinkText}>
            <FormattedMessage
              id="templates.jwm_site_workspace_link.linked-projects-2"
              defaultMessage="Linked projects"
              description="Displays associated projects within the workspace environment."
            />
          </p>
          <div className={styles.joinButtonWrapper}>
            <JoinJiraButton
              workspaceId={workspaceId}
              cloudId={jwmLinkData?.idCloud}
              navigationData={jwmLinkNavigationData}
              analyticsSource={analyticsSource}
              setJoined={setJoined}
            />
          </div>
        </div>
      ) : (
        <a
          href={jwmLinkNavigationData.jwmSiteUrl}
          target="_blank"
          aria-label={intl.formatMessage({
            id: 'templates.jwm_site_workspace_link.aria-label-workspace-jwm-link-spork',
            defaultMessage:
              'View projects in the linked Jira site. Opens external site.',
            description:
              'Access linked Jira site projects; opens an external site.',
          })}
          onClick={openJwmLinkedSite}
          className={cx(
            styles.workspaceLink,
            isNewWorkspacePage && styles.newWorkspacePage,
          )}
        >
          <div className={styles.shortcutIconFlagEnabled}>
            <ShortcutIcon
              label=""
              // @ts-expect-error
              color={dynamicToken('dynamic.icon')}
              size="small"
            />
          </div>
          <p className={styles.workspaceLinkText}>
            <FormattedMessage
              id="templates.jwm_site_workspace_link.linked-projects-2"
              defaultMessage="Linked projects"
              description="Displays a list of associated projects for the workspace."
            />
          </p>
        </a>
      )}
      {renderInviteExperience()}
    </>
  );
};
